import type {BuilderSlotType, DalValue, RefInfo, SchemaService} from '@wix/document-services-types'
import {getDetailedValueByPath} from './getDetailedValueByPath'
import {specialReferences, unmarkedReferenceBaseData, unmarkedReferences} from './constants/constants'
import _ from 'lodash'
import type {
    ExtendedRefInfo,
    LiteRefInfo,
    PartialRefInfo,
    ReferredDataItemsIterateeContext,
    UnmarkedRefInfo
} from './types'

type ReferredDataItemsIteratee = (context: ReferredDataItemsIterateeContext) => void

interface ForEachReferredDataItemOptions {
    originalItem?: DalValue
}

type GroupedReferredItems = Record<string, DalValue[]>

const getRootRefInfo = (namespace: string, schemaService: SchemaService): PartialRefInfo => ({
    path: [namespace],
    isRelationalSplit: schemaService.getNamespaceConfig(namespace)?.isRelationalSplitFromQuery
})

const getValuesByPath = (ownerItem: DalValue, path: string[] | RefInfo['path']): DalValue[] =>
    getDetailedValueByPath(ownerItem, path).map(({value}) => value)

const groupReferredItems = (items: DalValue[]): GroupedReferredItems => _.groupBy(items, ({type}) => type)

const isBuilderSlotType = (type: unknown): type is BuilderSlotType =>
    // eslint-disable-next-line lodash/prefer-lodash-typecheck
    typeof type === 'string' && type.endsWith('.Slots')

const getUnmarkedReferencesForItem = (namespace: string, itemType: string): UnmarkedRefInfo[] => {
    if (isBuilderSlotType(itemType)) {
        return [{referencedMap: 'components', path: ['slots', 'children']}]
    }
    return unmarkedReferences[namespace]?.[itemType] ?? []
}

const forEachReferredDataItemInternal = (
    schemaService: SchemaService,
    item: DalValue,
    namespace: string,
    iteratee: ReferredDataItemsIteratee,
    {originalItem}: ForEachReferredDataItemOptions = {},
    ownerItem?: DalValue,
    itemRefInfo: PartialRefInfo = getRootRefInfo(namespace, schemaService)
) => {
    const schemaRefFieldsInfo: readonly LiteRefInfo[] = schemaService.extractReferenceFieldsInfo(namespace, item.type)

    const itemUnmarkedReferences = getUnmarkedReferencesForItem(namespace, item.type)

    const unmarkedSchemaRefFieldsInfo = itemUnmarkedReferences.map((ref: UnmarkedRefInfo) => ({
        ...unmarkedReferenceBaseData,
        ...ref
    }))
    const allRefFieldsInfo = [...schemaRefFieldsInfo, ...unmarkedSchemaRefFieldsInfo].map((ref: ExtendedRefInfo) => {
        const path = ref.path.join('.')
        return {
            ...ref,
            ..._.get(specialReferences, [namespace, '*', path]),
            ..._.get(specialReferences, [namespace, item.type, path])
        }
    })

    iteratee({item, namespace, itemRefInfo, refFieldsInfo: allRefFieldsInfo, ownerItem, originalItem})

    for (const refInfo of schemaRefFieldsInfo) {
        const {isRefOwner, path, referencedMap} = refInfo
        if (!isRefOwner) {
            continue
        }

        // sometimes reference is not defined and is valid to be null
        const referredItems = getValuesByPath(item, path).filter(v => v !== null)
        const originalReferredItems = getValuesByPath(originalItem, path).filter(v => v !== null)

        if (referredItems.length === 0) {
            continue
        }

        const originalReferredItemsGrouped = groupReferredItems(originalReferredItems)
        for (const referredItem of referredItems) {
            const originalReferredItem = originalReferredItemsGrouped[referredItem.type]?.shift()

            forEachReferredDataItemInternal(
                schemaService,
                referredItem,
                referencedMap,
                iteratee,
                {originalItem: originalReferredItem},
                item,
                refInfo
            )
        }
    }
}

export const forEachReferredDataItem = (
    schemaService: SchemaService,
    item: DalValue,
    namespace: string,
    iteratee: ReferredDataItemsIteratee,
    options?: ForEachReferredDataItemOptions
) => forEachReferredDataItemInternal(schemaService, item, namespace, iteratee, options)
